import {
  CloseButton,
  CrudMode,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  deepCopy,
  newToast,
} from "@compass/core";
import { Select, SubmitButton } from "components";
import {
  LkStudentDisplayFieldId,
  Permission,
  Student,
  StudentApplicationFilterResource,
  StudentApplicationListResource,
  StudentApplicationResponseDto,
  StudentEnrollment,
} from "generated/openApi";
import { ApplicationStatus } from "pages/cobro/applications/ApplicationPropertyEnum";
import { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import ApiService from "services/apiService";

type StudentApplicationHeader = {
  rootPermission: Permission;
  crudMode: CrudMode;
  applicationDto: StudentApplicationResponseDto | undefined;
  updateApplicationDto: (dto: StudentApplicationResponseDto) => void;
  isApproving: boolean;
  canReview: boolean;
  isReviewing: boolean;
  hasChanges: boolean;
  applicationResource: StudentApplicationListResource;
  updateIsApproving: (value: boolean) => void;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  newStudent: Student;
  reviewStudentEnrollment: StudentEnrollment | undefined;
  exportApplication: () => void;
};

export const StudentApplicationHeader = ({
  rootPermission,
  crudMode,
  applicationDto,
  isApproving,
  canReview,
  isReviewing,
  hasChanges,
  applicationResource,
  updateIsApproving,
  updateApplicationDto,
  setValidationErrors,
  unsavedChanges,
  newStudent,
  reviewStudentEnrollment,
  exportApplication,
}: StudentApplicationHeader) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [resource, setResource] = useState<StudentApplicationFilterResource>({ permissionToApprove: false });
  const [menu, setMenu] = useState<MenuItemType[]>([]);
  const listContext = useContext<ListContextValue>(ListContext);


  useEffect(() => {
    ApiService.studentApplications.getFilterResource().then((response) => {
      setResource(response);
    });
  }, []);

  useEffect(() => {
    if (applicationDto) {
      let menuItems: MenuItemType[] = [];
      resource.applicationStatuses?.map((s) => {
        if (s.id == ApplicationStatus.Approved && applicationDto?.applicationStatus?.id != ApplicationStatus.Approved) {
          menuItems.push({
            label: s.name,
            action: () => approveApplicationStart(),
          });
        }
        if (s.id == ApplicationStatus.Waitlisted && applicationDto?.applicationStatus?.id != ApplicationStatus.Approved && applicationDto?.applicationStatus?.id != ApplicationStatus.Waitlisted) {
          menuItems.push({
            label: s.name,
            action: () => waitListApplication(),
          });
        }
        if (s.id == ApplicationStatus.Pending && applicationDto?.applicationStatus?.id != 2 && applicationDto?.applicationStatus?.id != ApplicationStatus.Pending) {
          menuItems.push({
            label: s.name,
            action: () => pendingApplication(),
          });
        }
        if (s.id == ApplicationStatus.Declined && applicationDto?.applicationStatus?.id != ApplicationStatus.Approved && applicationDto?.applicationStatus?.id != ApplicationStatus.Declined) {
          menuItems.push({
            label: <Typography color="error">{s.name}</Typography>,
            action: () => rejectApplication(),
          });
        }
        if (s.id > 4 && s.id != ApplicationStatus.StaffReview) {
          menuItems.push({
            label: s.name,
            action: () => updateApplicationStatus(s.id),
          });
        }
      })
      setMenu(menuItems);
    }
  }, [applicationDto]);

  const saveUpdateApplicationResponse = () => {
    ApiService.studentApplications
      .updateApplicationStatus(applicationDto!)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            navigate("../");
            window.location.reload();
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  }

  const applicationStatusField = () => {
    let filteredOptions = resource?.applicationStatuses;
    if (!resource.permissionToApprove) {
      filteredOptions = filteredOptions?.filter(o => o.id != ApplicationStatus.Approved);
    }

    return <Select
      label="Status"
      sx={{ width: "200px" }}
      // errors={validationErrors["reviewerId"]}
      value={resource?.applicationStatuses?.find((x) => x.id === applicationDto?.applicationStatusId)}
      getOptionLabel={(option) => `${option.name}`}
      options={filteredOptions}
      onChange={(e, value) => {
        updateApplicationDto({...applicationDto, applicationStatusId: value.id});
      }}
    />
  };

  // creates the student and sets the application to approved
  const save = () => {
    let hasCustomValidationErrors = false;
    let customValidationErrors = {};
    if (applicationResource.displayFields?.find((x) => x.id === LkStudentDisplayFieldId.ProgramStatus) && !applicationDto?.applicationResponse?.programStatusId) {
      hasCustomValidationErrors = true;
      customValidationErrors = {
        ...customValidationErrors,
        "programStatusId": ["Program Status Required"]
      };
    }

    if (hasCustomValidationErrors) {
      setValidationErrors(customValidationErrors);
      dispatch(setAppToast(newToast("error", "Validation Error")));

      const element = document.getElementById(`review-card`);
      if (element) {
        element.scrollIntoView(true);
      }
    }

    if (!hasCustomValidationErrors) {
      ApiService.students
        .saveCreate(newStudent!)
        .then((studentId) => {
          approveApplication(studentId);
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    }
  };

  // creates a partial student (for staff review) and sets the application to Staff Review
  const saveReview = () => {
    let hasCustomValidationErrors = false;
    let customValidationErrors = {};
    if (applicationResource.displayFields?.find((x) => x.id === LkStudentDisplayFieldId.ProgramStatus) && !applicationDto?.applicationResponse?.programStatusId) {
      hasCustomValidationErrors = true;
      customValidationErrors = {
        ...customValidationErrors,
        "programStatusId": ["Program Status Required"]
      };
    }
    if (!applicationDto?.applicationResponse?.reviewerId) {
      hasCustomValidationErrors = true;
      customValidationErrors = {
        ...customValidationErrors,
        "reviewerId": ["Reviewer Required"]
      };
    }

    if (hasCustomValidationErrors) {
      setValidationErrors(customValidationErrors);
      dispatch(setAppToast(newToast("error", "Validation Error")));

      const element = document.getElementById(`review-card`);
      if (element) {
        element.scrollIntoView(true);
      }
    }
    if (!hasCustomValidationErrors) {
      ApiService.students
        .saveCreate({ ...newStudent!, isPartial: true })
        .then((studentId) => {
          updateReviewApplication(studentId);
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          setValidationErrors(ex.data);
        });
    }
  };

  // updates a partial student (still non-Partial) and keeps the application as staff review (i.e when reviewing, and still want to keep it as reviewing)
  const updateReview = () => {
    setValidationErrors({});

    ApiService.students
      .saveUpdate({ ...newStudent!, isPartial: true })
      .then((studentId) => {
        updateReviewApplication(studentId);
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  // updates a partial student (sets them to non-Partial) and sets the application to approved
  const finishReview = () => {
    setValidationErrors({});

    ApiService.students
      .saveUpdate({ ...newStudent!, isPartial: undefined })
      .then((studentId) => {
        ApiService.studentEnrollments.saveUpdate(reviewStudentEnrollment!)
          .then(() => {
            approveApplication(studentId);
          })
      })
      .catch((ex) => {
        dispatch(setAppToast(newToast("error")));
        setValidationErrors(ex.data);
      });
  };

  const updateApplicationStatus = (applicationStatusId: number) => {
    updateIsApproving(false);
    var temp = { ...applicationDto, applicationStatusId: applicationStatusId };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const rejectApplication = () => {
    updateIsApproving(false);
    var temp = { ...applicationDto, applicationStatusId: ApplicationStatus.Declined };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const approveApplicationStart = () => {
    updateIsApproving(true);
    var temp = deepCopy(applicationDto!);
    temp.applicationStatus!.name = "Approving...";
    updateApplicationDto(temp);
  };

  const reviewApplicationStart = () => {
    updateIsApproving(true);
    var temp = deepCopy(applicationDto!);
    temp.applicationStatus!.name = "Entering Information...";
    temp.applicationStatusId = ApplicationStatus.StaffReview;
    updateApplicationDto(temp);
  };

  const updateReviewApplication = (studentId: number) => {
    var temp = { ...applicationDto, applicationStatusId: applicationDto?.applicationStatusId!, studentId: studentId };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            navigate("../");
            window.location.reload();
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const approveApplication = (studentId: number) => {
    var temp = { ...applicationDto, applicationStatusId: ApplicationStatus.Approved, studentId: studentId };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            navigate("../");
            window.location.reload();
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const waitListApplication = () => {
    updateIsApproving(false);
    var temp: StudentApplicationResponseDto = { ...applicationDto, applicationStatusId: ApplicationStatus.Waitlisted };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  const pendingApplication = () => {
    updateIsApproving(false);
    var temp: StudentApplicationResponseDto = { ...applicationDto, applicationStatusId: ApplicationStatus.Pending };
    ApiService.studentApplications
      .updateApplicationStatus(temp)
      .then((r) => {
        ApiService.studentApplications
          .getApplicationResponse(applicationDto?.applicationResponse?.id)
          .then((response) => {
            updateApplicationDto(response);
            dispatch(setAppToast(newToast("success")));
          });
      })
      .catch((ex) => dispatch(setAppToast(newToast("error", ex.response))));
  };

  return (
    <>
      {applicationDto && (
        <Stack direction="row" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing="sm">
            <Typography variant="inherit">
              {applicationDto.lastName}, {applicationDto.firstName}
            </Typography>
            {((!canReview || !isApproving)) && (<Typography fontWeight="light">Application Status: {applicationDto.applicationStatus?.name}</Typography>)}
            {(menu.length > 0 && !canReview) && <DropDownMenu menuItems={menu!} />}
            {menu.length > 0 && canReview && isApproving && (<Stack>{applicationStatusField()}</Stack>)}
          </Stack>

          <Stack direction="row" alignItems="center">
            <CloseButton />
            {/* {(crudMode === "view" && !isApproving) && isAuthorized(rootPermission, PermissionAction.Update) && (
              <UpdateButton onClick={() => {}} />
            )} */}
            {/* <SubmitButton
              onClick={() => exportApplication()}
              label="Export"
            /> */}
            {/* For moving to the student data entry portion during the review process */}
            {isReviewing && !isApproving && canReview && <UpdateButton label="Continue Review" onClick={() => reviewApplicationStart()} disabled={!unsavedChanges} />}

            {/* For moving to the student data entry portion during the review process */}
            {!isReviewing && !isApproving && canReview && applicationDto.applicationStatusId != ApplicationStatus.Approved 
                && <UpdateButton label="Review" onClick={() => reviewApplicationStart()} disabled={!unsavedChanges} />}

            {/* For creating a partial student and setting the status to "Staff Review" */}
            {!isReviewing && isApproving && canReview && applicationDto.applicationStatusId == ApplicationStatus.StaffReview 
                && <UpdateButton label="Flag for Review" onClick={() => saveReview()} disabled={!unsavedChanges} />}

            {/* For creating a new student and marking the application as approved */}
            {isApproving && ((!isReviewing && !canReview) || (canReview && applicationDto.applicationStatusId == ApplicationStatus.Approved && (!applicationDto.studentId || applicationDto.studentId == 0))) 
              && <SubmitButton label="Approve" onClick={() => save()} disabled={!unsavedChanges || !resource.permissionToApprove} />}

            {/* For just updating the review (does not approve) */}
            {isApproving && isReviewing && applicationDto.applicationStatusId == ApplicationStatus.StaffReview
              && <UpdateButton label="Update Review" onClick={() => updateReview()} disabled={!unsavedChanges || !applicationDto.applicationStatusId} />}
              
            {/* For finishing the review (approves the application) */}
            {isApproving && canReview && applicationDto.applicationStatusId == ApplicationStatus.Approved && applicationDto.studentId && applicationDto.studentId > 0
              && <SubmitButton label="Approve" onClick={() => finishReview()} disabled={!unsavedChanges || !resource.permissionToApprove} />}
            
            {/* For just updating the application status (not staff review or approve) */}
            {hasChanges && (applicationDto.applicationStatusId == ApplicationStatus.Declined || applicationDto.applicationStatusId == ApplicationStatus.Waitlisted || applicationDto.applicationStatusId == ApplicationStatus.Pending)
              && <SubmitButton onClick={() => saveUpdateApplicationResponse()} disabled={!unsavedChanges} />}
          </Stack>
        </Stack>
      )}
    </>
  );
};
